import { FC } from "react";
import { GroupedInspectionItem } from "@/types/Inspection.type.ts";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "@/features/inspection-list/components/styles/InspectionCardItem.module.scss";
import { ROUTES } from "@/route/routes.constants";
import { SuccessStatusIcon, FailedStatusIcon, ProgressStatusIcon, PendingStatusIcon } from '@/assets/icons/svgs';
import { useAppSelector } from "@/store/hooks";
import { InspectUploadStatus } from "@/types/InspectUploadStatus";
import { useStoredInspection } from "@/hooks/useStoredInspection";
import { useNetwork } from "@/hooks/useNetwork";
import { inspectionNeedsApiHydration } from "@/utils/ensureInspectionHydrated";
import { toast } from "react-toastify";
import { InspectionOwner } from "@/enums/InspectionOwner.enum";
import { useSelector } from "react-redux";
import { RootState } from "@/store/rootReducer";
import { APP_CONFIG_KEY } from "@/types/AppConfig.type";

export const InspectionCardItem: FC<GroupedInspectionItem> = ({
    colours,
    internalOrderReference,
    externalOrderReference,
    lotSize,
    submissionNumber,
    factory,
    id: currentInspectionId,
    isFullDetailPreloaded,
    inspectionStatusId,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isOnline = useNetwork();
    const storedInspection = useStoredInspection(currentInspectionId);
    const isLockedForUpload = storedInspection?.locked?.by === InspectionOwner.SW;
    const isOfflineLiteUnavailable =
        !isOnline &&
        inspectionNeedsApiHydration({ isFullDetailPreloaded, inspectionStatusId }) &&
        storedInspection == null;
    const config = useSelector((state: RootState) => state.appConfig);

    // Get sync status from Redux state
    const { inspectionId: currentUploadingId, toUpload } = useAppSelector(state => state.uploadProgress);

    const getStatusIcon = () => {
        // Find the current inspection in the upload queue
        const currentInspectionInQueue = toUpload?.find(inspection => inspection.id === currentInspectionId);
        const isStoredLocally = !!storedInspection;

        switch (true) {
            case toUpload?.some(inspection => inspection.id === currentInspectionId && inspection.status === InspectUploadStatus.Success):
                return <SuccessStatusIcon />;
            case toUpload?.some(inspection => inspection.id === currentInspectionId && inspection.status === InspectUploadStatus.Failed):
                return <FailedStatusIcon />;
            case currentInspectionId === currentUploadingId:
                return <ProgressStatusIcon />;
            case currentInspectionInQueue?.status === InspectUploadStatus.Pending || isStoredLocally:
                return <PendingStatusIcon />;
            default:
                return null;
        }
    }


    function getRow(
        firstLabel: string,
        firstVal: string | number,
        secondLabel?: string,
        secondVal?: string | number,
        icon?: React.ReactNode
    ) {
        return (
            <Grid
                size={12}
                sx={{ paddingTop: 0, color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.9rem' }}
            >
                <div
                    style={{ marginTop: !secondLabel ? '0.67rem' : '' }}
                    className={styles.rowContainer}>
                    <div
                        className={styles.rowItem}
                    >
                        <p className={styles.rowItemLabel}>{t(firstLabel)}:</p> <p className={styles.rowItemValue}>{firstVal}</p>
                    </div>
                    {secondLabel && (
                        <div
                            className={styles.rowItem}
                        >
                            <p className={styles.rowItemLabel}>{t(secondLabel)}:</p> <p className={styles.rowItemValue}>{secondVal}</p>
                        </div>
                    )}
                    {icon && <span className={styles.statusIcon}>{icon}</span>}
                </div>
            </Grid>
        );
    }
    const handleClick = () => {
        if (isOfflineLiteUnavailable) {
            toast.info(t('inspection_hydration_requires_connection'));
            return;
        }
        if (isLockedForUpload) {
            toast.error(t('inspection_locked_for_upload'));
            return;
        }
        navigate(`${ROUTES.INSPECTIONS}/${currentInspectionId}/start`);
    };

    const showExternalPOFilter = config.filters?.some(filter => filter === 'externalPO');
    const showInternalRef = config[APP_CONFIG_KEY.showInternalOrderRef];
    const firstLabel = showInternalRef ? 'internal_order_reference' : 'Colour';
    const firstValue = showInternalRef ? internalOrderReference : colours;

    return (
        <>

            <Grid
                container
                className={`${styles.itemContainer} ${isLockedForUpload ? styles.lockedForUpload : ''} ${isOfflineLiteUnavailable ? styles.offlineLiteUnavailable : ''}`}
                onClick={handleClick}
                title={isOfflineLiteUnavailable ? t('inspection_hydration_requires_connection') : undefined}
                aria-disabled={isOfflineLiteUnavailable ? true : undefined}
            >

                {showExternalPOFilter ?
                    getRow(firstLabel, firstValue, 'external_order_reference', externalOrderReference, getStatusIcon()) :
                    getRow(firstLabel, firstValue, 'order_reference', internalOrderReference, getStatusIcon())}

                {getRow('submission_number', submissionNumber, 'lot_size', lotSize)}
                {config[APP_CONFIG_KEY.showAssignedLocationInspections] && getRow('Factory', factory?.name || '', 'Location', factory?.location?.name || '')}
            </Grid>
        </>
    );
};
